import { Preferences } from '@capacitor/preferences';

import { purgeLegacyVault } from '@ninety/mobile/platform/auth';

/**
 * Everything an earlier version of the app left on the device that nothing reads any more.
 *
 * `biometricCredentials` is the one that matters: it held `{ email, password }` as JSON in
 * UserDefaults / SharedPreferences — the person's actual password in plaintext. This branch does the
 * same job through the **keychain** instead, via `BiometricSignIn.saveCredentials`, so nothing writes
 * this key any more and what it left behind has to go.
 *
 * `hasLoggedOut` gated `main`'s launch-time prompt. Biometrics are a button now, so nothing reads it.
 *
 * **`biometricSettings` is deliberately absent, and must stay absent.** It is the enrollment
 * preference, kept under `main`'s key so an already-enrolled device upgrades with no migration code —
 * see `ENROLLMENT_KEY`. This function runs unconditionally at every cold start rather than once, so
 * adding it here would un-enroll every enrolled user, and would do it again on every launch.
 */
const LEGACY_PREFERENCES = ['biometricCredentials', 'hasLoggedOut'];

const LEGACY_WEB_STORAGE = ['ACCESS-TOKEN', 'ENTITLEMENTS-TOKEN'];

/**
 * Delete what an earlier version of the app persisted.
 *
 * The spec claims "no credentials in plaintext anywhere" and "no tokens in `localStorage` or cookies
 * on native". Both are true of what this code *writes*, and neither is true of a device that ran an
 * earlier version, because deleting the writer does not delete what it already wrote. A phone that had
 * the app installed yesterday is still holding a plaintext password until something removes it.
 *
 * Note what this deliberately does not do: migrate. Nothing here reads the session format an earlier
 * version wrote, so the first launch after an upgrade signs the person out once — which the spec lists
 * as an accepted Forced Sign-out. This only removes what is left behind afterwards.
 *
 * Every failure is swallowed per key. On a platform where a store is unavailable, or a key was never
 * written, there is nothing to do and nothing to report — and a purge that could fail the launch
 * would be a worse bug than the one it is cleaning up.
 *
 * The Vault this branch replaced is purged from here too, rather than from its own launch hook. Both
 * are the same job — residue left by a writer that no longer exists — and one call site is one place
 * for the launch to await, where two would be two chances for the second to be forgotten.
 */
export async function purgeLegacyCredentials(): Promise<void> {
  for (const key of LEGACY_PREFERENCES) {
    await Preferences.remove({ key }).catch(() => undefined);
  }

  for (const key of LEGACY_WEB_STORAGE) {
    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } catch {
      // Storage disabled or unavailable. Nothing was readable here either, then.
    }
  }

  await purgeLegacyVault();
}
